import { useTranslation } from 'react-i18next'
import type { Appointment, Client } from '@/lib/api'
import { parseTime } from './calendarHelpers'
import { SLOT_HEIGHT, MINUTES_PER_SLOT } from './constants'
import { DEFAULT_EVENT_COLOR } from './clientColors'

export interface AppointmentEventProps {
  /** 预约数据 */
  appt: Appointment
  /** 关联学员 */
  client?: Client | null
  /** 课程块颜色（调用方按学员着色规则算好后传入，缺省用默认蓝） */
  color?: string
  /** 覆盖样式（top/height/left/width） */
  style?: React.CSSProperties
  /** 是否可拖拽 */
  draggable?: boolean
  /** 点击回调 */
  onClick?: (appt: Appointment) => void
  /** 拖拽开始回调 */
  onDragStart?: (e: React.DragEvent, appt: Appointment) => void
}

export function AppointmentEvent({
  appt,
  client,
  color,
  style,
  draggable = false,
  onClick,
  onDragStart,
}: AppointmentEventProps) {
  const { t } = useTranslation()
  const displayTitle = appt.title || client?.name || t('schedule.untitled')
  const baseColor = color || DEFAULT_EVENT_COLOR
  const computedTop = (parseTime(appt.startTime) / MINUTES_PER_SLOT) * SLOT_HEIGHT + 2
  const computedHeight = Math.max(
    ((parseTime(appt.endTime) - parseTime(appt.startTime)) / MINUTES_PER_SLOT) * SLOT_HEIGHT - 4,
    SLOT_HEIGHT / 2,
  )

  return (
    <div
      data-testid={`appointment-event-${appt.id}`}
      draggable={draggable}
      onDragStart={(e) => onDragStart?.(e, appt)}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(appt)
      }}
      style={{
        top: computedTop,
        height: computedHeight,
        left: 'calc(0% + 2px)',
        width: 'calc(100% - 4px)',
        ...style,
        backgroundColor: `${baseColor}1A`, // 10% opacity hex
        borderLeft: `3px solid ${baseColor}`,
      }}
      className="absolute rounded px-1.5 py-0.5 text-xs overflow-hidden cursor-pointer select-none hover:brightness-95"
    >
      <div className="font-medium truncate leading-tight" style={{ color: baseColor }}>
        {displayTitle}
      </div>
      {appt.packageName && <div className="text-slate-500 truncate leading-tight text-[10px]">{appt.packageName}</div>}
      <div className="text-slate-600 truncate leading-tight">
        {appt.startTime} - {appt.endTime}
      </div>
      {appt.location && <div className="text-slate-500 truncate leading-tight">{appt.location}</div>}
    </div>
  )
}

export function MonthAppointmentEvent({
  appt,
  client,
  color,
  onClick,
}: Omit<AppointmentEventProps, 'style' | 'draggable' | 'onDragStart'>) {
  const { t } = useTranslation()
  const displayTitle = appt.title || client?.name || t('schedule.untitled')
  const baseColor = color || DEFAULT_EVENT_COLOR

  return (
    <div
      data-testid={`month-appointment-event-${appt.id}`}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(appt)
      }}
      className="text-xs px-1 py-0.5 rounded mb-0.5 truncate cursor-pointer hover:brightness-95"
      style={{ backgroundColor: `${baseColor}1A`, color: baseColor, borderLeft: `2px solid ${baseColor}` }}
    >
      {appt.startTime} {displayTitle}
    </div>
  )
}
